import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import dateFns from 'date-fns';
import { Grid, Typography } from '@mui/material';
import { numeralFormat } from './lib';
import { Select } from '../';
import useStyles from './styles';
import { defaultfreq } from './constants';
import { getRange, getFrequencyOptions, getDefaultFrequency, getLayout, getPeriodValue, getDate, getDateInTheRange, getDestructuringDate, isValidNumber } from './lib';
import { withBlank } from '../utils';

var Period = function Period(_ref) {
  var _ref$defaultFrequency = _ref.defaultFrequency,
      defaultFrequency = _ref$defaultFrequency === undefined ? defaultfreq : _ref$defaultFrequency,
      _ref$availableFrequen = _ref.availableFrequencies,
      availableFrequencies = _ref$availableFrequen === undefined ? [defaultfreq] : _ref$availableFrequen,
      _boundaries = _ref.boundaries,
      _availableBoundaries = _ref.availableBoundaries,
      changePeriod = _ref.changePeriod,
      changeFrequency = _ref.changeFrequency,
      _period = _ref.period,
      labels = _ref.labels,
      periodDisabled = _ref.periodDisabled,
      frequencyDisabled = _ref.frequencyDisabled,
      disabled = _ref.disabled;

  var classes = useStyles();
  var frequencies = getFrequencyOptions(availableFrequencies, labels);
  if (R.isEmpty(frequencies)) return null;
  var frequency = getDefaultFrequency({
    defaultFrequency: defaultFrequency,
    frequencies: R.pluck('value')(frequencies)
  });
  var period = R.defaultTo([], _period);
  var boundaries = R.defaultTo([], _boundaries);
  var availableBoundaries = R.defaultTo([], _availableBoundaries);
  var isPeriodDisabled = R.or(R.any(R.isEmpty)([period, boundaries]), periodDisabled);
  var startDate = R.head(period);
  var endDate = R.last(period);

  var startPeriodBoundaries = [R.head(boundaries), R.defaultTo(R.last(boundaries), endDate)];
  var endPeriodBoundaries = [R.defaultTo(R.head(boundaries), startDate), R.last(boundaries)];
  var ranges = [getRange(startPeriodBoundaries, startDate), getRange(endPeriodBoundaries, endDate)];

  var showFrequency = R.and(R.gt(R.length(frequencies), 1), R.not(frequencyDisabled));
  var destructuringDates = R.map(getDestructuringDate)(period);

  var onChangeFrequency = function onChangeFrequency(value) {
    if (R.is(Function, changeFrequency)) return changeFrequency(value);
  };
  var _getIsDisabled = function _getIsDisabled(getter) {
    return function (value, id) {
      var _getDate;

      var fn = R.prop(getter)({ start: R.head, end: R.last });
      if (!isValidNumber(Number(value)) || R.length(availableBoundaries) !== 2) {
        return false;
      }
      var dateValue = getPeriodValue(fn(destructuringDates));

      var date = getDate(frequency, dateValue, (_getDate = {}, _getDate[id] = Number(value), _getDate), getter === 'end');
      var availableStart = availableBoundaries[0],
          availableEnd = availableBoundaries[1];

      if (id === 'year') {
        if (dateFns.isSameYear(date, availableStart)) {
          date = availableStart;
        } else if (dateFns.isSameYear(date, availableEnd)) {
          date = availableEnd;
        }
      }
      return dateFns.compareAsc(date, availableStart) === -1 || dateFns.compareDesc(date, availableEnd) === -1;
    };
  };
  var onChangePeriod = function onChangePeriod(getter, id) {
    return function (value) {
      var fn = R.prop(getter)({ start: R.head, end: R.last });
      var lensIndex = R.prop(getter)({ start: R.lensIndex(0), end: R.lensIndex(1) });
      var dateValue = getPeriodValue(fn(destructuringDates));
      var selectedValue = R.ifElse(isValidNumber, R.assoc(id, R.__, {}), R.always(undefined))(Number(value));
      var newDate = getDate(frequency, dateValue, selectedValue, getter === 'end');
      if (R.is(Function, changePeriod)) {
        return changePeriod(R.pipe(R.set(lensIndex, newDate), function (_ref2) {
          var startDate = _ref2[0],
              endDate = _ref2[1];
          return [getDateInTheRange(startPeriodBoundaries)(startDate), getDateInTheRange(endPeriodBoundaries)(endDate)];
        })(period));
      }
    };
  };
  return React.createElement(
    Grid,
    { 'data-testid': 'period-picker-test-id', container: true, direction: 'row' },
    showFrequency && React.createElement(
      Grid,
      {
        item: true,
        xs: 12,
        className: classes.headerContainer,
        'data-testid': 'period-picker-frequency-test-id'
      },
      React.createElement(
        Grid,
        { container: true, direction: 'row' },
        React.createElement(
          Grid,
          { item: true, xs: 12 },
          React.createElement(Select, {
            id: labels.frequency,
            label: labels.frequency,
            onChange: onChangeFrequency,
            value: frequency,
            items: frequencies
          })
        )
      )
    ),
    R.not(isPeriodDisabled) && R.map(function (_ref3) {
      var getter = _ref3.getter,
          layout = _ref3.layout,
          label = _ref3.label,
          value = _ref3.value,
          testId = _ref3.testId,
          getIsDisabled = _ref3.getIsDisabled;
      return React.createElement(
        Grid,
        { key: getter, item: true, xs: 12, 'data-testid': testId },
        React.createElement(
          Grid,
          { container: true, direction: 'row' },
          React.createElement(
            Grid,
            { item: true, xs: 12, className: classes.sideLabel },
            React.createElement(
              Typography,
              {
                variant: 'body2',
                tabIndex: 0 // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
                , 'aria-label': label
              },
              label
            )
          )
        ),
        React.createElement(
          Grid,
          { container: true, direction: 'row' },
          React.createElement(
            Grid,
            { item: true, xs: 12 },
            React.createElement(
              Grid,
              { container: true, className: classes.headerContainer, spacing: 1 },
              R.map(function (row) {
                return R.map(function (_ref4) {
                  var id = _ref4.id,
                      values = _ref4.values;

                  var selectedValue = R.isNil(R.prop(id)(value)) ? R.head(values) : R.prop(id)(value);
                  return React.createElement(
                    Grid,
                    {
                      key: id,
                      item: true,
                      xs: 12 / R.length(row),
                      className: classes.selectContainer
                    },
                    React.createElement(Select, {
                      onChange: onChangePeriod(getter, id),
                      label: R.prop(id)(labels),
                      id: id + '-' + label,
                      dataTestId: id + '-' + label + '-test-id',
                      value: selectedValue,
                      disabled: disabled || false,
                      items: R.map(function (option) {
                        return {
                          id: id,
                          value: option,
                          disabled: getIsDisabled(option, id),
                          label: R.ifElse(R.is(String), R.identity, R.prop(id)(numeralFormat))(option)
                        };
                      })(values)
                    })
                  );
                })(row);
              })(layout)
            )
          )
        )
      );
    })([{
      getter: 'start',
      testId: 'period-picker-start-period-test-id',
      layout: getLayout(frequency, R.head(ranges)),
      label: R.prop('start')(labels),
      value: getPeriodValue(R.head(destructuringDates)),
      getIsDisabled: _getIsDisabled('start')
    }, {
      getter: 'end',
      testId: 'period-picker-end-period-test-id',
      layout: getLayout(frequency, R.last(ranges), /*R.last(availableRanges)*/[]),
      label: R.prop('end')(labels),
      value: getPeriodValue(R.last(destructuringDates)),
      getIsDisabled: _getIsDisabled('end')
    }])
  );
};

Period.propTypes = process.env.NODE_ENV !== "production" ? {
  defaultFrequency: PropTypes.string,
  availableFrequencies: PropTypes.array,
  availableBoundaries: PropTypes.array,
  boundaries: PropTypes.array,
  changePeriod: PropTypes.func,
  changeFrequency: PropTypes.func,
  period: PropTypes.array,
  labels: PropTypes.object,
  periodDisabled: PropTypes.bool,
  frequencyDisabled: PropTypes.bool,
  disabled: PropTypes.bool
} : {};

export default withBlank(function (_ref5) {
  var periodDisabled = _ref5.periodDisabled,
      frequencyDisabled = _ref5.frequencyDisabled,
      isBlank = _ref5.isBlank;
  return {
    isBlank: R.or(isBlank, R.and(periodDisabled, frequencyDisabled))
  };
})(Period);